import { useEffect, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { api } from '../api';
import { HeadActions } from '../components/Layout';

const CHECKS = [
  { key: 'ptz', label: 'PTZ' },
  { key: 'blur', label: 'Blur' },
  { key: 'sd', label: 'SD Data' },
  { key: 'reboot', label: 'Reboot' },
  { key: 'speaker', label: 'Speaker' },
  { key: 'resolved', label: 'Resolved' },
];

const today = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kuala_Lumpur' });

const shiftDate = (value, days) => {
  const [year, month, day] = String(value).split('-').map(Number);
  const next = new Date(Date.UTC(year, month - 1, day + days));
  return next.toISOString().slice(0, 10);
};

function Mark({ item }) {
  if (item?.value === 'pending') {
    return (
      <span className="health-mark pending" title={item.detail || 'Not yet due'}>
        —
      </span>
    );
  }
  if (item?.value === 'fail') {
    return (
      <span className="health-mark fail" title={item.detail || 'Failed'}>
        ✕
      </span>
    );
  }
  return (
    <span className="health-mark pass" title={item?.detail || 'Passed'}>
      ✓
    </span>
  );
}

export default function Health() {
  const ctx = useOutletContext();
  const [date, setDate] = useState(today());
  const [report, setReport] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const requestId = useRef(0);

  const load = async (nextDate = date, refresh = false) => {
    const id = requestId.current + 1;
    requestId.current = id;
    setLoading(true);
    setError('');
    try {
      const data = refresh ? await api.healthRefresh(nextDate) : await api.health(nextDate);
      if (id !== requestId.current) return;
      setReport(data);
    } catch (err) {
      if (id !== requestId.current) return;
      setError(err.message);
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  };

  useEffect(() => {
    load(date);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDate = (value) => {
    const current = today();
    const next = value > current ? current : value;
    if (!next || next === date) return;
    setDate(next);
    load(next);
  };

  const goPrev = () => onDate(shiftDate(date, -1));
  const goNext = () => onDate(shiftDate(date, 1));
  const goToday = () => onDate(today());
  const isToday = date >= today();

  const refresh = async () => {
    setBusy(true);
    try {
      await load(date, true);
    } finally {
      setBusy(false);
    }
  };

  const exportExcel = async () => {
    setExporting(true);
    setError('');
    try {
      const { blob, filename } = await api.exportHealth(date);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    } finally {
      setExporting(false);
    }
  };

  const cards = [
    {
      key: 'am',
      label: 'MORNING',
      value: report ? `${report.summary.morning.online}/${report.summary.cameras}` : '—',
      sub: 'Online at 9:00 AM',
    },
    {
      key: 'pm',
      label: 'AFTERNOON',
      value: report ? `${report.summary.afternoon.online}/${report.summary.cameras}` : '—',
      sub: 'Online at 4:00 PM',
    },
    {
      key: 'resolved',
      label: 'RESOLVED',
      value: report ? report.summary.morning.resolved + report.summary.afternoon.resolved : '—',
      sub: 'No issue at the check',
    },
    {
      key: 'cameras',
      label: 'CAMERAS',
      value: report?.summary.cameras ?? '—',
      sub: report?.dateLabel || 'Selected day',
      accent: true,
    },
  ];

  return (
    <>
      <div className="page-head">
        <div>
          <h1>CCTV Health</h1>
          <p>
            {report?.note ||
              'PTZ is updated from DSS records at 09:00 (AM) and 15:00 (PM). Downtime under 10 minutes is not counted.'}
          </p>
        </div>
        <HeadActions {...ctx}>
          <button type="button" className="btn" onClick={exportExcel} disabled={exporting || loading}>
            {exporting ? 'Export…' : 'Export Excel'}
          </button>
          <button type="button" className="btn btn-gold" onClick={refresh} disabled={busy}>
            {busy ? 'Checking…' : 'Run check'}
          </button>
        </HeadActions>
      </div>

      {error && <p className="error">{error}</p>}

      <div className="stat-row">
        {cards.map((card) => (
          <article key={card.key} className={`stat-card${card.accent ? ' accent' : ''}`}>
            <div className="stat-kicker">{card.label}</div>
            <div className="stat-value">{card.value}</div>
            <div className="stat-sub">{card.sub}</div>
          </article>
        ))}
      </div>

      <section className="panel">
        <div className="panel-head">
          <strong>Daily check</strong>
          <div className="toolbar">
            <div className="date-nav">
              <button
                type="button"
                className="icon-btn"
                onClick={goPrev}
                aria-label="Previous day"
                title="Previous day"
              >
                ‹
              </button>
              <input
                type="date"
                className="date-input"
                value={date}
                max={today()}
                onChange={(e) => onDate(e.target.value)}
                aria-label="Check date"
              />
              <button
                type="button"
                className="icon-btn"
                onClick={goNext}
                disabled={isToday}
                aria-label="Next day"
                title="Next day"
              >
                ›
              </button>
              <button type="button" className="btn" onClick={goToday} disabled={isToday}>
                Today
              </button>
            </div>
          </div>
        </div>

        <div className="health-legend">
          <span>
            <span className="health-mark pass">✓</span> Working / Data available
          </span>
          <span>
            <span className="health-mark fail">✕</span> Not working / Camera blur
          </span>
          <span>
            <span className="health-mark na">—</span> Not yet due
          </span>
        </div>

        {loading ? (
          <div className="empty">Loading daily check…</div>
        ) : !report?.devices?.length ? (
          <div className="empty">
            <div className="empty-icon">◎</div>
            No cameras. Sync devices from DSS first.
          </div>
        ) : (
          <div className="table-wrap">
            <table className="data health-table">
              <thead>
                <tr>
                  <th rowSpan={2}>NO.</th>
                  <th rowSpan={2}>CAMERA / LOCATION</th>
                  <th colSpan={6} className="health-group">
                    Morning (9:00 AM)
                  </th>
                  <th colSpan={6} className="health-group">
                    Afternoon (4:00 PM)
                  </th>
                </tr>
                <tr>
                  {CHECKS.map((check) => (
                    <th key={`am-${check.key}`}>{check.label}</th>
                  ))}
                  {CHECKS.map((check) => (
                    <th key={`pm-${check.key}`}>{check.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {report.devices.map((row) => (
                  <tr key={row.deviceId}>
                    <td>{row.bil}</td>
                    <td>{row.deviceName}</td>
                    {['morning', 'afternoon'].flatMap((slot) =>
                      CHECKS.map((check) => (
                        <td key={`${slot}-${check.key}-${row.deviceId}`} className="health-cell">
                          <Mark item={row[slot][check.key]} />
                        </td>
                      ))
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}
